import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import {
  Alert, Button, Dialog, EmptyState, IconButton, PageHeader, Pill, Select, Skeleton, TextField, Textarea, useConfirm, useToast,
} from '../ui';
import { EditIcon, ListIcon, PlusIcon, SortIcon, TrashIcon } from '../ui/icons';
import { sectionCrumb } from '../settings/SectionLayout';

const PRIORITIES = ['low', 'medium', 'high', 'critical'];
const STATUSES = ['proposed', 'accepted', 'in_progress', 'testing', 'completed', 'rejected'];
const PRIORITY_TONES = { critical: 'danger', high: 'amber', medium: 'accent', low: 'green' };
const STATUS_TONES = { proposed: 'neutral', accepted: 'accent', in_progress: 'amber', testing: 'amber', completed: 'green', rejected: 'danger' };
const SORTABLE = ['title', 'priority', 'release_version', 'status', 'created_at'];

/** /admin/backlog — proposals for the application (deprecated page, kept for administrators). */
function BacklogList() {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProposal, setEditingProposal] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  const fetchProposals = async () => {
    try {
      const response = await api.get('/backlog-proposals');
      setProposals(response.data);
    } catch (err) {
      console.error('Error fetching backlog proposals:', err);
      setError(t('backlog.errors.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProposals();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- once, on arrival

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sorted = [...proposals].sort((a, b) => {
    let aValue = a[sortField] ?? '';
    let bValue = b[sortField] ?? '';
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();
    const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    return sortDirection === 'desc' ? -comparison : comparison;
  });

  const handleDelete = async (proposal) => {
    if (!(await confirm({
      title: t('backlog.confirmDeleteTitle'),
      message: t('backlog.confirmDelete', { title: proposal.title }),
      confirmLabel: t('backlog.deleteButton'),
      danger: true,
    }))) return;
    try {
      await api.delete(`/backlog-proposals/${proposal.id}`);
      await fetchProposals();
    } catch (err) {
      console.error('Error deleting proposal:', err);
      setError(t('backlog.errors.delete'));
    }
  };

  const handleModalSave = async (proposalData) => {
    try {
      if (editingProposal) {
        await api.put(`/backlog-proposals/${editingProposal.id}`, proposalData);
      } else {
        await api.post('/backlog-proposals', proposalData);
      }
      await fetchProposals();
      setShowModal(false);
      setEditingProposal(null);
    } catch (err) {
      console.error('Error saving proposal:', err);
      setError(t('backlog.errors.save'));
    }
  };

  const label = (group, value) => (value ? t(`backlog.${group}.${value}`, { defaultValue: value }) : '');
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString(i18n.resolvedLanguage || 'en');
  const create = () => { setEditingProposal(null); setShowModal(true); };
  const sortHeader = (field) => {
    const active = sortField === field;
    return (
      <th role="columnheader" scope="col" aria-sort={active ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}>
        <button type="button" className={`item-table__sort${active ? ' is-active' : ''}`} onClick={() => handleSort(field)}>
          {t(`backlog.col.${field}`)}
          <SortIcon size={14} className={active ? undefined : 'item-table__arrow is-idle'} />
        </button>
      </th>
    );
  };

  const header = (
    <PageHeader
      breadcrumb={[sectionCrumb(t, 'admin'), { label: t('nav.backlog') }]}
      title={t('backlog.title')}
      actions={<Button icon={PlusIcon} onClick={create}>{t('backlog.create')}</Button>}
    />
  );
  if (loading) return <>{header}<Skeleton height={240} radius="var(--radius-lg)" /></>;

  return (
    <div>
      {header}
      <div className="settings-stack">
        {error && <Alert tone="danger">{error}</Alert>}
        {proposals.length === 0 ? (
          <EmptyState icon={ListIcon} title={t('backlog.emptyTitle')} action={<Button icon={PlusIcon} onClick={create}>{t('backlog.create')}</Button>}>
            {t('backlog.empty')}
          </EmptyState>
        ) : (
          <div className="data-table-wrap">
            <table role="table" className="data-table data-table--stack">
              <thead>
                <tr role="row">
                  {SORTABLE.map(field => <React.Fragment key={field}>{sortHeader(field)}</React.Fragment>)}
                  <th role="columnheader" scope="col"><span className="visually-hidden">{t('accounts.col.actions')}</span></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((proposal) => (
                  <tr role="row" key={proposal.id}>
                    <th role="rowheader" scope="row">
                      {proposal.title}
                      {proposal.description && (
                        <span className="backlog__description">
                          {proposal.description.length > 100 ? `${proposal.description.substring(0, 100)}…` : proposal.description}
                        </span>
                      )}
                    </th>
                    <td role="cell" data-label={t('backlog.col.priority')}><Pill tone={PRIORITY_TONES[proposal.priority] || 'neutral'}>{label('priority', proposal.priority)}</Pill></td>
                    <td role="cell" data-label={t('backlog.col.release_version')}>{proposal.release_version || '—'}</td>
                    <td role="cell" data-label={t('backlog.col.status')}><Pill tone={STATUS_TONES[proposal.status] || 'neutral'}>{label('status', proposal.status)}</Pill></td>
                    <td role="cell" data-label={t('backlog.col.created_at')} className="accounts__date">{formatDate(proposal.created_at)}</td>
                    <td role="cell" className="data-table__actions">
                      <div className="accounts__actions">
                        <IconButton icon={EditIcon} size="sm" variant="ghost" label={t('backlog.editNamed', { title: proposal.title })}
                                    onClick={() => { setEditingProposal(proposal); setShowModal(true); }} />
                        <IconButton icon={TrashIcon} size="sm" variant="ghost" className="field-row__remove"
                                    label={t('backlog.deleteNamed', { title: proposal.title })} onClick={() => handleDelete(proposal)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <BacklogProposalModal
          proposal={editingProposal}
          onSave={handleModalSave}
          onCancel={() => { setShowModal(false); setEditingProposal(null); }}
        />
      )}
    </div>
  );
}

/** Creating or editing a proposal. */
function BacklogProposalModal({ proposal, onSave, onCancel }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [formData, setFormData] = useState({
    title: proposal?.title || '',
    description: proposal?.description || '',
    priority: proposal?.priority || 'medium',
    release_version: proposal?.release_version || '',
    status: proposal?.status || 'proposed',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error(t('backlog.form.titleRequired'));
      return;
    }
    onSave(formData);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <Dialog
      open
      onClose={onCancel}
      title={proposal ? t('backlog.form.editTitle') : t('backlog.create')}
      size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onCancel}>{t('common.cancel')}</Button>
          <Button type="submit" form="backlog-proposal">{proposal ? t('common.save') : t('backlog.form.create')}</Button>
        </>
      )}
    >
      <form id="backlog-proposal" className="dialog-form" onSubmit={handleSubmit}>
        <TextField name="title" label={t('backlog.col.title')} value={formData.title} onChange={handleInputChange} required
                   placeholder={t('backlog.form.titlePlaceholder')} />
        <Textarea name="description" label={t('backlog.form.description')} rows={4} value={formData.description} onChange={handleInputChange}
                  placeholder={t('backlog.form.descriptionPlaceholder')} />
        <div className="backlog__form-row">
          <Select name="priority" label={t('backlog.col.priority')} value={formData.priority} onChange={handleInputChange}>
            {PRIORITIES.map(value => <option key={value} value={value}>{t(`backlog.priority.${value}`)}</option>)}
          </Select>
          <TextField name="release_version" label={t('backlog.col.release_version')} value={formData.release_version}
                     onChange={handleInputChange} placeholder="v1.2.0" />
          <Select name="status" label={t('backlog.col.status')} value={formData.status} onChange={handleInputChange}>
            {STATUSES.map(value => <option key={value} value={value}>{t(`backlog.status.${value}`)}</option>)}
          </Select>
        </div>
      </form>
    </Dialog>
  );
}

export default BacklogList;
